// **نيّةُ فتح** — «روح الشاشة الفلانية، **وأوّل ما توصل** افتح فيها كذا».
//
// (قرار المالك 22/09/2026: «خلّي ريكويست جديد دي تحت في قائمة على طول، واعمل كمان زرار
// جنبه اسمه تسجيل متابعة جديدة بحيث يضغط عليه يفتحله على صفحة تسجيل المتابعة على طول».)
//
// والمشكلة التي تحلّها: زرُّ «حجز جديد» يعيش في شاشة الحجوزات وحالتُه فيها. فمن ضغط
// «حجز جديد» من الصفحة الرئيسية يريد **النموذج مفتوحًا**، لا شاشةَ الحجوزات ثم بحثًا عن
// الزرّ فيها. ولا سبيل إلى ذلك برفع الحالة إلى `App`: تلك حالةُ شاشةٍ لا حالةُ تطبيق،
// ورفعُها يفتح بابًا لرفع كل حالةٍ بعدها.
//
// **فالنيّة تُترك في الطريق وتُلتقط عند الوصول**: تُكتب في تخزين الجلسة ويُبثّ حدث.
// الشاشةُ المقصودة تقرؤها عند تركيبها (فتعمل حين لا تكون مفتوحة)، وتسمع الحدث (فتعمل
// حين تكون مفتوحةً فعلًا). وهو نفس منطق `jumpToSection` القائم — الفرق أن ذاك يختار
// قسمًا وهذا يطلب فعلًا.
//
// **وتُستهلك مرّةً واحدة**: تُمحى فور تنفيذها. وإلّا فتح تحديثُ الصفحة النموذجَ من
// جديد كلّما رجع المستخدم إلى الشاشة، بلا أن يطلب.
import { useEffect, useRef } from 'react';

const KEY = 'icontravel.openIntent';
const EVENT = 'icontravel:open-intent';

/** اطلب فعلًا في شاشةٍ أخرى، ثم انتقل إليها. */
export function requestOpen(page: string, what: string): void {
  const v = `${page}:${what}`;
  try { window.sessionStorage.setItem(KEY, v); } catch { /* تصفّح خاص: يبقى الحدث وحده */ }
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: v })); } catch { /* بيئة بلا CustomEvent */ }
}

/**
 * نفِّذ `run` إن كانت النيّةُ المعلَّقة تخصّ هذه الشاشة وهذا الفعل — عند التركيب أو
 * حين تصل وهي مفتوحة. و`run` تُقرأ من مرجعٍ فلا يُعاد ربط المستمع مع كل رسم.
 */
export function useOpenIntent(page: string, what: string, run: () => void): void {
  const ref = useRef(run);
  ref.current = run;

  useEffect(() => {
    const want = `${page}:${what}`;
    const take = (): void => {
      let v: string | null = null;
      try { v = window.sessionStorage.getItem(KEY); } catch { return; }
      if (v !== want) return;
      try { window.sessionStorage.removeItem(KEY); } catch { /* لا يضرّ: الفعل يقع مرّة */ }
      ref.current();
    };
    take();
    window.addEventListener(EVENT, take);
    return () => window.removeEventListener(EVENT, take);
  }, [page, what]);
}

/**
 * نيّةٌ تحمل قيمة: `page:prefix:<value>` — كفتح حجزٍ بعينه من إشعار (0190). نفس الآلية، والقيمة تُسلَّم للفعل.
 */
export function useOpenIntentWith(page: string, prefix: string, run: (value: string) => void): void {
  const ref = useRef(run);
  ref.current = run;
  useEffect(() => {
    const want = `${page}:${prefix}:`;
    const take = (): void => {
      let v: string | null = null;
      try { v = window.sessionStorage.getItem(KEY); } catch { return; }
      if (!v || !v.startsWith(want)) return;
      try { window.sessionStorage.removeItem(KEY); } catch { /* */ }
      ref.current(v.slice(want.length));
    };
    take();
    window.addEventListener(EVENT, take);
    return () => window.removeEventListener(EVENT, take);
  }, [page, prefix]);
}
